import { signal, WritableSignal } from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { SwarmSchedule } from "../s25-swarm-schedule/SwarmSchedule";
import { ProfileUtil } from "../s25-datepattern/profile.util";
import { BOARD_CONST } from "../s25-swarm-schedule/s25.board.const";
import { EventSummary } from "../s25-swarm-schedule/s25.event.summary.service";
import DowChar = EventSummary.DowChar;
import { MPG } from "./s25.meeting.pattern.grid.component";
import { MeetingPatternGrid } from "../s25-swarm-schedule/meeting.pattern.grid.service";
import { BoardOccUtil } from "../s25-swarm-schedule/s25.board.occ.util";
import { Proto } from "../../pojo/Proto";
import { DowGrid } from "../s25-virtual-grid/s25.dow.grid.component";

export namespace MPGUtil {
    import ISODateString = Proto.ISODateString;

    export function atPercentClosest<T>(arr: T[], percent: number): T {
        const index = S25Util.clamp(Math.round((percent / 100) * arr.length), 0, arr.length - 1);
        return arr[index];
    }

    export function moveOccurrences(
        data: Readonly<Pick<MPG.Item["mpg"], "occs" | "profileCode">>,
        dowCandidate: Readonly<DowGrid.Times>,
    ): MPG.ItemData["occs"] {
        if (!data.occs?.length) return data.occs ?? [];
        const start = S25Util.date.parse(S25Util.date.toS25ISOTimeStrFromHours(dowCandidate.startHour));
        const end = S25Util.date.parse(S25Util.date.toS25ISOTimeStrFromHours(dowCandidate.endHour));
        return BoardOccUtil.getOccurrences({
            dow: dowCandidate.dow as SwarmSchedule.DayPatterns,
            start,
            end,
            profileCode: data.profileCode,
            occs: data.occs.map((occ) => ({
                reservation_start_dt: new Date(occ.reservation_start_dt),
                reservation_end_dt: new Date(occ.reservation_end_dt),
            })),
        }).map((occ) => ({
            reservation_start_dt: S25Util.date.toS25ISODateTimeStrFromDate(occ.reservation_start_dt),
            reservation_end_dt: S25Util.date.toS25ISODateTimeStrFromDate(occ.reservation_end_dt),
        }));
    }

    export function applyCommittedMpg(item: MPG.Item): void {
        const committed = item.mpg.candidate() ?? item.mpg.effective();
        item.position.set(committed);
        item.mpg.sourceRoomId = committed.roomId;
        item.mpg.occs = MPGUtil.effectiveOccs(item);
        item.mpg.profileCode = MPGUtil.effectiveProfileCode(item);
        item.mpg.noShadows.set(committed.roomId === -1);
    }

    export function effectiveOccs(item: MPG.Item): MPG.ItemData["occs"] {
        return MPGUtil.moveOccurrences(item.mpg, item.mpg.effective());
    }

    export function effectiveProfileCode(item: MPG.Item): MPG.ItemData["profileCode"] {
        return MPGUtil.getNewProfileCode(item.mpg.profileCode, item.mpg.effective().dow);
    }

    export function syncedIds(item: MPG.Item): Set<MPG.Item["id"]> {
        return new Set(
            (item.mpg.event?.linkedItems ?? [])
                .filter((linked) => linked.sync)
                .map((linked) => linked.objRef.uuid)
                .filter((uuid) => uuid != null),
        );
    }

    export function areSynced(first: MPG.Item, second: MPG.Item): boolean {
        return syncedIds(first).has(second.id) || syncedIds(second).has(first.id);
    }

    export function syncedItems(primary: MPG.Item, items: readonly MPG.Item[]): MPG.Item[] {
        const itemById = new Map(items.map((item) => [item.id, item]));
        const synced: MPG.Item[] = [primary];
        const seen = new Set<MPG.Item["id"]>([primary.id]);

        for (const linkedId of syncedIds(primary)) {
            const linked = itemById.get(linkedId);
            if (!linked?.draggable?.()) continue;
            if (seen.has(linked.id)) continue;
            seen.add(linked.id);
            synced.push(linked);
        }

        return synced;
    }

    export function itemsOverlap(a: MPG.Item, b: MPG.Item): boolean {
        if (!(a.draggable?.() || b.draggable?.())) return false;
        if (areSynced(a, b)) return false;

        const aPos = a.mpg.effective();
        const bPos = b.mpg.effective();
        if (bPos.startHour >= aPos.endHour || bPos.endHour <= aPos.startHour) {
            return false;
        }
        return doOccurrencesOverlap(MPGUtil.effectiveOccs(a), MPGUtil.effectiveOccs(b));
    }

    export function mapGridItemToItemTo(
        item: MPG.Item,
        position = item.mpg.effective(),
        profileCode = MPGUtil.getNewProfileCode(item.mpg.profileCode, position.dow),
    ): SwarmSchedule.EventI {
        const { roomId, dow, startHour, endHour } = position;
        const { uuid, sourceRoomId } = item.mpg;

        return {
            itemUUID: uuid!,
            sourceRoomId: sourceRoomId!,
            start: S25Util.date.toS25ISOTimeStrFromHours(startHour),
            end: S25Util.date.toS25ISOTimeStrFromHours(endHour),
            profileCode,
            dow,
            roomId: Number(roomId),
            rowCol: {
                rowUUID: String(roomId),
                colUUID: `${dow}-${Math.floor(startHour)}`,
            },
            timestamp: Date.now(),
        };
    }

    export function getNewProfileCode(currentCode: string, dow: string) {
        const model = ProfileUtil.getProfileModel(
            null,
            currentCode,
            <any>ProfileUtil.getProfileCodeThroughDate(currentCode),
        );

        const dows = new Set(dow.split("").map((d: DowChar) => BOARD_CONST.dowMapInverse[d]));
        for (const day of model.daysOfWeek!) day.chosen = dows.has(day.abbr);

        return ProfileUtil.getProfileCodeByModel(model);
    }

    export function getItemTos(items: readonly MPG.Item[]): SwarmSchedule.EventI[] {
        return items.map((item) => mapGridItemToItemTo(item));
    }

    export function getCommittedItemTos(items: readonly MPG.Item[]): SwarmSchedule.EventI[] {
        return items.map((item) => mapGridItemToItemTo(item, item.position(), item.mpg.profileCode));
    }

    export function mapLocationToRow(location: SwarmSchedule.BoardRoom): MPG.Row {
        const { roomId, roomName, maxCapacity, partition } = location;
        const features = location.features || [];
        const hidden = signal(false);
        return {
            id: String(roomId),
            hidden: hidden.asReadonly(),
            mpg: {
                name: roomName + (roomId === -1 ? "" : ` (${maxCapacity})`),
                features: features.map((feature) => ({
                    id: feature.featureId,
                    name: feature.featureName,
                })),
                partition: partition && {
                    id: partition.partitionId,
                    name: partition.partitionName,
                },
                maxCapacity: maxCapacity || 0,
                hidden,
                roomId,
            },
        };
    }

    export function getItemData(
        event: MeetingPatternGrid.Item,
    ): MPG.ItemData & { position: WritableSignal<MPG.Position> } {
        return {
            initialized: false,
            position: signal({
                roomId: event.roomId ?? -1,
                dow: event.dow,
                startHour: S25Util.date.timeToHours(event.startTime),
                endHour: S25Util.date.timeToHours(event.endTime),
            }),
            name: event.itemName!,
            uuid: event.uuid,
            event,
            profileCode: event.profileCode,
            sourceRoomId: event.roomId,
            lastChange: 0,
            organization: event.objRef!.organization && {
                id: event.objRef!.organization.organizationId,
                name: event.objRef!.organization.organizationName,
                partitions: event.objRef!.organization.partitions?.map((partition) => ({
                    priority: partition.priority,
                    list: partition.list?.map((p) => ({ id: p.partitionId, name: p.partitionName })) || [],
                })),
            },
            features:
                event
                    .objRef!.features?.filter((f) => f)
                    .map((feature) => ({
                        id: feature.featureId,
                        name: feature.featureName,
                    })) || [],
            headCount: event.headCount || 0,
            occs: event.occs,
            noShadows: signal(event.roomId === -1),
            activeUser: signal(undefined),
        };
    }

    /**
     * Scores rows by relevance for assigning the current item to a new location
     */
    export function scoreRows(rows: MPG.Row[], item: MPG.Item, minFillRatio: number): Map<MPG.Row["id"], number> {
        const features = new Set(item.mpg.features.map((feature) => feature.id));

        const partitionPriority: Record<number, number> = {};
        for (const { priority, list } of item.mpg.organization?.partitions || []) {
            for (const part of list) partitionPriority[part.id] = priority;
        }

        return new Map(
            rows.map((row) => {
                const score = scoreRow(
                    Number(row.id),
                    row.mpg,
                    { headCount: item.mpg.headCount, roomId: item.position().roomId },
                    minFillRatio,
                    features,
                    partitionPriority,
                );
                return [row.id, score];
            }),
        );
    }

    export function scoreRow(
        roomId: number,
        rowData: Pick<MPG.RowData, "features" | "partition" | "maxCapacity">,
        roomData: Pick<MPG.ItemData, "headCount"> & { roomId: number | undefined },
        minFillRatio: number,
        features: Set<number>,
        partitionPriority: Record<number, number>,
    ): number {
        const matchingFeatures = S25Util.array.count(rowData.features, (feature) => +features.has(feature.id));

        const priority = rowData.partition?.id ? partitionPriority[rowData.partition.id] : undefined;
        const partitionScore = priority ? 1 / priority : 0;

        const unusedHeadcount = rowData.maxCapacity - roomData.headCount;
        let utilizationScore = 1 / (Math.abs(unusedHeadcount) || 0.9);
        if (unusedHeadcount < 0) utilizationScore -= 0.0001;
        if (roomData.headCount / (rowData.maxCapacity || 0.001) < minFillRatio) utilizationScore -= 1;

        const unassignedRowScore = roomId === -1 ? 1000 : 0;
        const itemRowScore = roomData.roomId === roomId ? 100 : 0;
        const rowScore = unassignedRowScore + itemRowScore;

        return matchingFeatures + partitionScore + utilizationScore + rowScore;
    }

    export function updateItemTimestamps(items: readonly MPG.Item[]) {
        for (const item of items) item.mpg.lastChange = Date.now();
    }

    /**
     * Check whether two sets of occurrences have any overlap
     * @param A SORTED array of occurrences
     * @param B SORTED array of occurrences
     */
    export function doOccurrencesOverlap<
        T extends { reservation_start_dt: ISODateString; reservation_end_dt: ISODateString }[],
    >(A: T, B: T) {
        if (!A?.length || !B?.length) return false;

        let a = 0;
        let b = 0;
        let occA = A[a];
        let occB = B[b];
        while (occA && occB) {
            if (occA.reservation_start_dt >= occB.reservation_end_dt) b++;
            else if (occA.reservation_end_dt <= occB.reservation_start_dt) a++;
            else return true;
            occA = A[a];
            occB = B[b];
        }

        return false;
    }
}
